import { FC, useEffect, useRef, useState } from 'react';
import styles from './GoodsDayPhoto.module.scss'
import { Modal } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import axios, { AxiosError } from 'axios';
import { envF } from './../../../../env'
import { Button } from '../../../../components/buttons/button';
import { useTranslation } from 'react-i18next';
import renderGlobalAlert from '../../../../system/hooks/useGlobalAlert';
import { PopoverClue } from '../../../../components/popoverClue/PopoverClue';

interface IGoodsDayPhoto {
    idGoodsGlobal: string;
    hasPhoto: boolean;
    isOpenedAccordion: boolean;
    lockFromPermission: boolean;
    onPhotoChange?: (hasPhoto: boolean) => void;
}

interface IErrorResponse {
    title: string;
    status: number;
    detail?: string;
    instance?: string;
}

const GoodsDayPhoto: FC<IGoodsDayPhoto> = ({
    idGoodsGlobal,
    hasPhoto,
    isOpenedAccordion,
    lockFromPermission,
    onPhotoChange,
}) => {
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [uploading, setUploading] = useState(false);
    const [currentPhotoUrl, setCurrentPhotoUrl] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { t } = useTranslation();
    const baseT = (value: string) => t('directory.goods.' + value)

    const jwtToken = localStorage.getItem("TokenData") as string;
    const token = `Bearer ${JSON.parse(jwtToken).accessToken}`;

    const fetchPhotoWithAuth = async () => {
        try {
            const response = await fetch(
                `${envF.REACT_APP_API_GATEWAY_PATH}/FileHub/GoodsDayPhoto/${idGoodsGlobal}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': token,
                    },
                }
            );

            if (response.ok) {
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                setCurrentPhotoUrl(blobUrl);
                return blobUrl;
            } else if (response.status === 404) {
                setCurrentPhotoUrl('');
                return null;
            } else {
                console.error('Failed to fetch photo:', response.status);
                return null;
            }
        } catch (error) {
            console.error('Error fetching photo:', error);
            return null;
        }
    };

    useEffect(() => {
        if (hasPhoto && isOpenedAccordion) {
            fetchPhotoWithAuth();
        } else if (!isOpenedAccordion) {
            if (currentPhotoUrl && currentPhotoUrl.startsWith('blob:')) {
                URL.revokeObjectURL(currentPhotoUrl);
            }
            setCurrentPhotoUrl('');
        }
        return () => {
            if (currentPhotoUrl && currentPhotoUrl.startsWith('blob:')) {
                URL.revokeObjectURL(currentPhotoUrl);
            }
        };

    }, [idGoodsGlobal, hasPhoto, isOpenedAccordion]);

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
        if (!isJpgOrPng) {
            renderGlobalAlert(
                {
                    variant: "danger",
                    statusCode: 200,
                    title: baseT('warnExtensionFile'),
                }
            )
            return;
        }
        await uploadPhoto(file);
    };

    const uploadPhoto = async (file: File) => {
        try {
            setUploading(true);

            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post(
                `${envF.REACT_APP_API_GATEWAY_PATH}/FileHub/GoodsDayPhoto/${idGoodsGlobal}`,
                formData,
                {
                    headers: {
                        Authorization: token,
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.status === 200 || response.status === 201 || response.status === 204) {
                renderGlobalAlert(
                    {
                        variant: "info",
                        statusCode: 200,
                        title: baseT('photoAdded'),
                    }
                )
                if (currentPhotoUrl && currentPhotoUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(currentPhotoUrl);
                }

                await fetchPhotoWithAuth();

                onPhotoChange?.(true);

                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        } catch (error: any) {
            console.error('Upload error:', error);
            
            if (axios.isAxiosError(error)) {
                handleAxiosError(error);
            } else {
                renderGlobalAlert({
                    variant: "danger",
                    statusCode: 500,
                    title: error?.message || baseT('errorUploadFile'),
                });
            }
            
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(
                `${envF.REACT_APP_API_GATEWAY_PATH}/FileHub/GoodsDayPhoto/${idGoodsGlobal}`,
                {
                    headers: {
                        Authorization: token,
                    },
                }
            );
            renderGlobalAlert(
                {
                    variant: "info",
                    statusCode: 200,
                    title: baseT('deletedPhoto'),
                }
            )

            if (currentPhotoUrl && currentPhotoUrl.startsWith('blob:')) {
                URL.revokeObjectURL(currentPhotoUrl);
            }

            onPhotoChange?.(false);
            
            setCurrentPhotoUrl('');
        } catch (error) {
            console.error('Delete error:', error);
            
            if (axios.isAxiosError(error)) {
                handleAxiosError(error);
            } else {
                renderGlobalAlert({
                    variant: "danger",
                    statusCode: 500,
                    title: baseT('errorDeleteFile'),
                });
            }
        }
    };

    const handlePreview = () => {
        if (currentPhotoUrl) {
            setPreviewImage(currentPhotoUrl);
            setPreviewOpen(true);
        }
    };

    const handleAxiosError = (error: AxiosError<IErrorResponse>) => {
        if (error.response) {
            const errorTitle = error.response.data?.title;

            if (errorTitle) {
                const cleanTitle = errorTitle.replace(/^"|"$/g, '');
                renderGlobalAlert({
                    variant: "danger",
                    statusCode: error.response.status,
                    title: cleanTitle,
                });
            } else {
                renderGlobalAlert({
                    variant: "danger",
                    statusCode: error.response.status,
                    title: baseT('errorUploadFile'),
                });
            }
        } else if (error.request) {
            renderGlobalAlert({
                variant: "danger",
                statusCode: 500,
                title: baseT('noServerResponse'),
            });
        } else {
            renderGlobalAlert({
                variant: "danger",
                statusCode: 500,
                title: error.message || baseT('errorUploadFile'),
            });
        }
    };

    return (
        <div className={styles.goodsPhotoWrapper}>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                disabled={uploading}
            />

            <div
                className={styles.photoContainer}
                onClick={currentPhotoUrl ? handlePreview : undefined}
                style={{ cursor: currentPhotoUrl ? 'pointer' : 'default' }}
            >
                {currentPhotoUrl ? (
                    <img
                        src={currentPhotoUrl}
                        alt="Goods photo"
                        className={styles.photo}
                    />
                ) : (
                    <div className={styles.placeholder}>
                        <PlusOutlined style={{ fontSize: '32px' }} />
                        <div>{baseT('noPhoto')}</div>
                    </div>
                )}
            </div>

            <div className={styles.buttonContainer}>
                <Button
                    variant="primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || lockFromPermission}
                >
                    {currentPhotoUrl ? baseT('replacePhoto') : baseT('addPhoto')}
                </Button>

                {currentPhotoUrl && (
                    <Button
                        variant="lightGray"
                        onClick={handleDelete}
                        disabled={uploading || lockFromPermission}
                    >
                        <DeleteOutlined style={{marginRight: 4, color: "#cc3333" }} />
                        {baseT('delPhoto')}
                    </Button>
                )}
            </div>
           <div className={styles.buttonAddContainer}>
                <PopoverClue
                    clues={[baseT('cluePhoto')]}
                />
            </div>
            <Modal
                open={previewOpen}
                title={baseT('viewPhoto')}
                footer={null}
                onCancel={() => setPreviewOpen(false)}
                width={'60%'}
            >
                <img
                    alt="preview"
                    className={styles.photoPreview}
                    src={previewImage}
                />
            </Modal>
        </div>
    );
};

export default GoodsDayPhoto;